import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Field, StepHeader, NavButtons } from "../common";
import { useSession } from "../../lib/session";

const SECTIONS = [
  ["complaints", "Chief Complaints & Description", [
    ["chief_complaints", "Chief complaints (with duration)", "e.g. Fever 4 days, breathlessness 1 day"],
    ["description", "Description / History of presenting illness", "Onset, progression, character, aggravating & relieving factors"],
  ]],
  ["pmh", "Past Medical & Surgical History", [
    ["past_medical", "Past medical history", "DM, HTN, CKD, COPD, TB, prior ICU stays..."],
    ["past_surgical", "Past surgical history", "Procedures, dates, complications"],
  ]],
  ["treatment", "Treatment History", [
    ["treatment_history", "Treatment received for the present illness", "Where treated, drugs / fluids / antibiotics / procedures given, duration and response"],
    ["drug_history", "Long-term medications", "Drug, dose, compliance, recent changes (steroids, anticoagulants, immunosuppressants)"],
  ]],
  ["family", "Family History", [["family", "Family history", "Similar illness, hereditary disease, sudden deaths"]]],
  ["personal", "Personal History", [["personal", "Personal history", "Smoking, alcohol, substance use, diet, sleep, bowel/bladder, occupation"]]],
  ["travel", "Travel & Allergy History", [
    ["travel", "Travel history", "Recent travel, endemic areas, animal/insect exposure"],
    ["allergy", "Allergy history", "Drug, food, latex - with reaction type"],
  ]],
];

const FEMALE = ["obgyn", "Menstrual & Obstetric History", [
  ["menstrual", "Menstrual history", "LMP, cycle, menorrhagia, menopause"],
  ["obstetric", "Obstetric history", "G P L A D, current pregnancy / postpartum status"],
]];

const ToggleGroup = ({ label, testid, options, value, onPick, onClass = "is-on", allowClear }) => (
  <div className="space-y-1.5">
    <div className="cc-label">{label}</div>
    <div className="flex gap-1" data-testid={testid}>
      {options.map(([val, text, tid]) => (
        <button key={val} data-testid={tid} onClick={() => onPick(allowClear && value === val ? "" : val)}
          className={`cc-toggle flex-1 ${value === val ? onClass : ""}`}>{text}</button>
      ))}
    </div>
  </div>
);

const SEX = ["Male", "Female", "Other"].map((s) => [s, s, `sex-${s.toLowerCase()}`]);
const BLOOD = ["A", "B", "AB", "O"].map((b) => [b, b, `blood-group-${b.toLowerCase()}`]);
const RH = [["Positive", "Rh +", "rh-positive"], ["Negative", "Rh −", "rh-negative"]];

const Demographics = () => {
  const { state, set } = useSession();
  const p = state.patient;
  return (
    <>
      <div className="grid sm:grid-cols-[2fr_1fr_1fr] gap-4 mb-8">
        <Field label="Name / ID (optional)" id="patient-name" value={p.name} onChange={(v) => set("patient.name", v)} />
        <Field label="Age" id="patient-age" type="number" unit="yrs" value={p.age} onChange={(v) => set("patient.age", v)} />
        <ToggleGroup label="Sex" testid="patient-sex-group" options={SEX} value={p.sex} onPick={(v) => set("patient.sex", v)} />
      </div>
      <div className="grid sm:grid-cols-[2fr_1fr] gap-4 -mt-4 mb-8">
        <ToggleGroup label="Blood group" testid="blood-group-group" options={BLOOD} value={p.blood_group} onClass="is-on-red" allowClear onPick={(v) => set("patient.blood_group", v)} />
        <ToggleGroup label="Rh factor" testid="rh-group" options={RH} value={p.rh} onClass="is-on-red" allowClear onPick={(v) => set("patient.rh", v)} />
      </div>
    </>
  );
};

export default function HistoryStep() {
  const { state, set } = useSession();
  const p = state.patient;
  const sections = p.sex === "Female" ? [...SECTIONS, FEMALE] : SECTIONS;
  const canNext = p.age && p.sex && state.history.chief_complaints?.trim();

  return (
    <div>
      <StepHeader num={1} title="Patient History" sub="Entered by the patient, attendant or clinician. Only chief complaints, age and sex are required." />
      <Demographics />
      <Accordion type="multiple" defaultValue={["complaints"]} className="border rounded-md bg-card">
        {sections.map(([key, title, fields]) => (
          <AccordionItem key={key} value={key} className="px-5">
            <AccordionTrigger data-testid={`accordion-${key}`} className="font-heading text-base">{title}</AccordionTrigger>
            <AccordionContent className="space-y-4 pb-6">
              {fields.map(([id, label, ph]) => (
                <Field key={id} id={id} label={label} placeholder={ph} multiline value={state.history[id]} onChange={(v) => set(`history.${id}`, v)} />
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <NavButtons nextDisabled={!canNext} nextLabel="Get focused questions" onNext={() => set("step", 1)} />
    </div>
  );
}
